import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import { Suspense, lazy, useEffect, useState } from "react";
import { MENUS } from "../../helpers/constants";
import Unauthorized from "./Unauthorized";
import Loader from "../common/Spinner";

// Lazy load components for better performance
const Dashboard = lazy(() => import("../dashboard/Dashboard"));
const UserMaster = lazy(() => import("../admin/UserMaster"));
const RoleMaster = lazy(() => import("../admin/RoleMaster"));
const LitigationControl = lazy(() => import("../accessControl/LitigationControl"));
const NoticesControl = lazy(() => import("../accessControl/NoticesControl"));
const ZoneMaster = lazy(() => import("../masters/ZoneMaster"));
const DivisionMaster = lazy(() => import("../masters/DivisionMaster"));
const BranchMaster = lazy(() => import("../masters/BranchMaster"));
const ApplicationStatusMaster = lazy(() => import("../masters/ApplicationStatusMaster"));
const DocumentsWithMaster = lazy(() => import("../masters/DocumentsWithMaster"));
const ActMaster = lazy(() => import("../masters/ActMaster"));
const DepartmentMaster = lazy(() => import("../masters/DepartmentMaster"));
const SuitFiledMaster = lazy(() => import("../masters/SuitFiledMaster"));
const IFPendingLocationMaster = lazy(() => import("../masters/IFPendingLocationMaster"));
const IssuingAuthorityMaster = lazy(() => import("../masters/IssuingAuthorityMaster"));
const PartyMaster = lazy(() => import("../masters/PartyMaster"));
const ControlMaster = lazy(() => import("../masters/ControlMaster"));
const CategoryMaster = lazy(() => import("../masters/CategoryMaster"));
const CourtMaster = lazy(() => import("../masters/CourtMaster"));
const LitigationMaster = lazy(() => import("../transactions/LitigationMaster"));
const LitigationForm = lazy(() => import("../transactions/LitigationForm"));
const Notices = lazy(() => import("../transactions/Notices"));
const NoticesForm = lazy(() => import("../transactions/NoticesForm"));
const Agreement = lazy(() => import("../transactions/Agreement"));
const AgreementForm = lazy(() => import("../transactions/AgreementForm"));
const Settlement = lazy(() => import("../transactions/Settlement"));
const SettlementForm = lazy(() => import("../transactions/SettlementForm"));
const PowerOfAttorney = lazy(() => import("../transactions/PowerOfAttorney"));
const PowerOfAttorneyForm = lazy(() => import("../transactions/PowerOfAttorneyForm"));
const IntellectualPropertyRights = lazy(() => import("../transactions/IntellectualPropertyRights"));
const IntellectualPropertyRightsForm = lazy(() => import("../transactions/IntellectualPropertyRightsForm"));

const HearingDetails = lazy(() => import("../transactions/HearingDetails"));
const PartyDetails = lazy(() => import("../transactions/PartyDetails"));
const PartyDetailsList = lazy(() => import("../transactions/PartyDetailsList"));
const PartyPaymentDetails = lazy(() => import("../transactions/PartyPaymentDetails"));

const NoticesReport = lazy(() => import("../reports/NoticesReport"));
const LitigationReport = lazy(() => import("../reports/LitigationReport"));
const NoticesReportNew = lazy(() => import("../reports/NoticesReport"));
const AgreementsReportNew = lazy(() => import("../reports/AgreementsReport"));
const AmountsSummaryReport = lazy(() => import("../reports/AmountsSummaryReport"));
const PowerOfAttorneyReport = lazy(() => import("../reports/PowerOfAttorneyReport"));
const LitigationSummaryReport = lazy(() => import("../reports/LitigationSummaryReport"));
const CertificateReport = lazy(() => import("../reports/CertificateReport"));
const IPRReport = lazy(() => import("../reports/IPRReport"));
const LitigationHelp = lazy(() => import("../helps/LitigationHelp"));
const UnderConstruction = lazy(() => import("../common/UnderConstruction"));

export default function Main(props) {
  const [authorizedPaths, setAuthorizedPaths] = useState([]);
  const location = useLocation();

  let sessions = sessionStorage.getItem("AuthorizedSessions");

  const restructureData = (data) => {
    if (!data) return {};
    const result = {};
    for (const key in data) {
      result[key] = {
        Menus: data[key].Menus.map(menu => ({
          SubMenu: menu.SubMenu,
          Path: menu.Path
        }))
      };
    }
    return result;
  };

  const parsedSessions = sessions ? restructureData(JSON.parse(sessions)) : {};

  const isAuthorized = () => {
    const allowedPaths = [];
    for (let key in MENUS) {
      const constantMenus = MENUS[key].Menus;
      const backendMenus = parsedSessions[key]?.Menus || [];

      constantMenus.forEach(constantMenu => {
        const isAuthorized = backendMenus.some(backendMenu =>
          backendMenu.SubMenu === constantMenu.SubMenu
        );
        if (isAuthorized) {
          allowedPaths.push(constantMenu.Path);
        }
      });
    }
    setAuthorizedPaths(allowedPaths);
  };

  useEffect(() => {
    isAuthorized();
  }, []);

  const ProtectedRoutes = () => {
    // Check for exact match first
    if (authorizedPaths.includes(location.pathname)) {
      return true;
    }

    // Check if current path matches any authorized pattern
    const currentPath = location.pathname;

    // If user has litigation access, allow all litigation-related routes
    if (authorizedPaths.includes('/transactions/litigation')) {
      if (currentPath.startsWith('/transactions/litigation') ||
        currentPath.startsWith('/transactions/hearings') ||
        currentPath.startsWith('/transactions/hearing-details') ||
        currentPath.startsWith('/transactions/party-details') ||
        currentPath.startsWith('/transactions/party-payment-details')) {
        return true;
      }
    }

    // If user has notices access, allow all notices-related routes
    if (authorizedPaths.includes('/transactions/notices')) {
      if (currentPath.startsWith('/transactions/notices')) {
        return true;
      }
    }

    // If user has agreements access, allow all agreements-related routes
    if (authorizedPaths.includes('/transactions/agreements')) {
      if (currentPath.startsWith('/transactions/agreements')) {
        return true;
      }
    }

    // If user has settlements access, allow all settlements-related routes
    if (authorizedPaths.includes('/transactions/settlements')) {
      if (currentPath.startsWith('/transactions/settlements')) {
        return true;
      }
    }

    // If user has power-of-attorney access, allow all power-of-attorney-related routes
    if (authorizedPaths.includes('/transactions/power-of-attorney')) {
      if (currentPath.startsWith('/transactions/power-of-attorney')) {
        return true;
      }
    }

    // If user has intellectual-property-rights access, allow all intellectual-property-rights-related routes
    if (authorizedPaths.includes('/transactions/intellectual-property-rights')) {
      if (currentPath.startsWith('/transactions/intellectual-property-rights')) {
        return true;
      }
    }

    // Check other authorized paths
    return authorizedPaths.some(authorizedPath => {
      return currentPath.startsWith(authorizedPath);
    });
  };

  return (
    <Container bsPrefix="main">
      <Routes>
        <Route exact path="/" element={
          <Suspense fallback={<Loader />}>
            <Dashboard sidebar={props.sidebar} setSidebar={props.setSidebar} />
          </Suspense>
        } />
        <Route exact path="/admin/role-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <RoleMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/admin/user-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <UserMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/access-control/litigation" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <LitigationControl />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/access-control/notices" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <NoticesControl />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/zone-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <ZoneMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/division-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <DivisionMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/branch-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <BranchMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/application-status-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <ApplicationStatusMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/litigation" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <LitigationMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/litigation/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <LitigationForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/litigation/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <LitigationForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/hearing-details/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <HearingDetails />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/party-details" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PartyDetailsList />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/party-details/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PartyDetails />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/party-details/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PartyDetails />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/party-payment-details/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PartyPaymentDetails />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/notices" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <Notices />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/notices/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <NoticesForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/notices/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <NoticesForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/agreements" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <Agreement />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/agreements/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <AgreementForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/agreements/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <AgreementForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/settlements" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <Settlement />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/settlements/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <SettlementForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/settlements/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <SettlementForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/power-of-attorney" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PowerOfAttorney />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/power-of-attorney/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PowerOfAttorneyForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/power-of-attorney/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PowerOfAttorneyForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/intellectual-property-rights" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <IntellectualPropertyRights />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/intellectual-property-rights/add" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <IntellectualPropertyRightsForm />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/transactions/intellectual-property-rights/edit" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <IntellectualPropertyRightsForm />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/master/documents-with-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <DocumentsWithMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/act-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <ActMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/department-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <DepartmentMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/suit-filed-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <SuitFiledMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/if-pending-location-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <IFPendingLocationMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/issuing-authority-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <IssuingAuthorityMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/party-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PartyMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/control-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <ControlMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/category-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <CategoryMaster />
          </Suspense>
        ) : <Unauthorized />
        } />
        <Route exact path="/master/court-master" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <CourtMaster />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/litigation" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <LitigationReport />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/notices" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <NoticesReportNew />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/agreements" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <AgreementsReportNew />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/amounts-summary" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <AmountsSummaryReport />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/power-of-attorney" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <PowerOfAttorneyReport />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/litigation-summary-report" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <LitigationSummaryReport />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/certificate" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <CertificateReport />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/reports/ipr" element={ProtectedRoutes() ? (
          <Suspense fallback={<Loader />}>
            <IPRReport />
          </Suspense>
        ) : <Unauthorized />
        } />

        <Route exact path="/help/litigation" element={
          <Suspense fallback={<Loader />}>
            <LitigationHelp />
          </Suspense>
        } />

        <Route exact path="/help/master" element={
          <Suspense fallback={<Loader />}>
            <UnderConstruction parentMenu="Help" menu="Master" />
          </Suspense>
        } />

        <Route exact path="/help/notices" element={
          <Suspense fallback={<Loader />}>
            <UnderConstruction parentMenu="Help" menu="Notices" />
          </Suspense>
        } />

        <Route exact path="/help/agreements" element={
          <Suspense fallback={<Loader />}>
            <UnderConstruction parentMenu="Help" menu="Agreements" />
          </Suspense>
        } />

        <Route exact path="/help/power-of-attorney" element={
          <Suspense fallback={<Loader />}>
            <UnderConstruction parentMenu="Help" menu="Power of Attorney" />
          </Suspense>
        } />

        <Route exact path="/help/queries" element={
          <Suspense fallback={<Loader />}>
            <UnderConstruction parentMenu="Help" menu="Queries" />
          </Suspense>
        } />

        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<Navigate to="/unauthorized" />} />
      </Routes>
    </Container>
  );
}